import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Input, Space, Tooltip, Card, Tag, Typography, Modal, Row, Col, Select, message } from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  EditOutlined,
  EyeOutlined,
  TagOutlined,
  PlusOutlined,
  FolderOutlined,
  DownOutlined,
  UpOutlined,
  BulbOutlined,
  LinkOutlined,
  CopyOutlined,
  ReloadOutlined,
  RobotOutlined,
  RocketOutlined
} from '@ant-design/icons';
import Icon from '@ant-design/icons';
import { getNoteIcon } from '../../utils/noteIcon';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';
import { apiHelpers, renderMarkdownSafely } from '../../pages/DashboardUtils';
import { isNoteOwner } from '../../pages/DashboardUtils';
import TranslateText from '../TranslateText';
import { isSectionVisibleOnCard, getSectionDisplayName } from '../../config/summarySections';
import { useDoubleClickProtection } from '../../hooks/useDoubleClickProtection';
import TiptapEditor from '../TiptapEditor/TiptapEditor';

// Custom Wand icon (matching Figma Wand2 design)
const WandSvg = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72Z"/>
    <path d="m14 7 3 3"/>
    <path d="M5 6v4"/>
    <path d="M19 14v4"/>
    <path d="M10 2v2"/>
    <path d="M7 8H3"/>
    <path d="M21 16h-4"/>
    <path d="M11 3H9"/>
  </svg>
);
const WandIcon = (props) => <Icon component={WandSvg} {...props} />;

const { Text, Title } = Typography;
const { Option } = Select;

const KnowledgeCard = ({
  // Note state management
  selectedNote, // Currently selected note object
  isEditing, // Whether note is in edit mode
  editTitle, // Edit mode title value
  setEditTitle, // Update edit title
  editSummary, // Edit mode summary value
  setEditSummary, // Update edit summary
  editContent, // Edit mode content value
  setEditContent, // Update edit content
  
  // User and action handlers
  user, // Current user object
  handleSave, // Save note changes
  handleCancel, // Cancel note editing
  handleDeleteNote, // Delete note handler
  handleRefreshNote, // Refresh note content
  handlePin, // Pin/unpin note handler
  handleEdit, // Enter edit mode handler
  
  // UI state management
  editTooltipVisible, // Edit tooltip visibility state
  setEditTooltipVisible, // Toggle edit tooltip
  isContentExpanded, // Content expansion state
  setIsContentExpanded, // Toggle content expansion
  isLoadingContent, // Loading state for content
  
  // Tag management
  isAddingTag, // Tag addition mode state
  setIsAddingTag, // Toggle tag addition mode
  handleFetchUserHistoryTags, // Fetch user's tag history
  newTagName, // New tag name input value
  setNewTagName, // Update new tag name
  handleAddTag, // Add tag to note handler
  userHistoryTags, // User's historical tags
  setUserHistoryTags, // Update user tags
  isLoadingHistoryTags, // Loading state for tags
  
  // Global state and content
  updateGlobalState, // Update global application state
  setIsUserSelectedNote, // Set user selected note flag
  setSelectedNote, // Update selected note state
  realContent, // Actual note content (processed from file paths)
  
  // Topic and knowledge base management
  noteTopicHierarchy, // Topic hierarchy for current note
  fetchAllKnowledgeBases, // Fetch all knowledge bases
  refreshKnowledgeBasesAndTopics, // Refresh KB and topics data
  
  // CardContent Modal handlers (optional)
  onNoteClick, // Handle note click from CardContent
  onQATopicClick, // Handle QA topic click from CardContent
  onTabChange, // Handle tab change from CardContent
  currentFilterState, // Current filter state for CardContent
  
  // Right panel visibility control
  isRightPanelVisible, // Whether right panel is visible
  onToggleRightPanel, // Toggle right panel visibility

  // Open New Note modal with AI Writer
  onOpenCreateModalWithAIWriter, // () => void, 打开 New Note 弹窗并设置 enableAIWriter=true

  // Right panel mode change
  onRightPanelModeChange // (mode: string) => void, switch right panel mode
}) => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);

  // 使用 ref 保存上一次选中的笔记 ID，用于检测笔记切换
  const previousNoteIdRef = useRef(selectedNote?.id);
  const toolButtonsContainerRef = useRef(null);

  // 监听笔记选择变化：如果处于编辑状态且笔记被切换，则取消编辑
  useEffect(() => {
    const currentNoteId = selectedNote?.id;
    const previousNoteId = previousNoteIdRef.current;

    // 如果笔记 ID 发生变化（新笔记被选中或取消选择）
    // previousNoteId !== undefined 确保不是首次渲染
    if (currentNoteId !== previousNoteId && previousNoteId !== undefined) {
      // 如果当前处于编辑状态，则取消编辑
      if (isEditing && handleCancel) {
        console.log('[KnowledgeCard] 检测到笔记切换，取消编辑状态:', {
          previousNoteId,
          currentNoteId,
          isEditing,
          noteTitle: selectedNote?.title || '无标题'
        });
        handleCancel();
      }
    }

    // 更新 ref 为当前笔记 ID（包括 null，用于处理取消选择的情况）
    previousNoteIdRef.current = currentNoteId;
  }, [selectedNote?.id, isEditing, handleCancel, selectedNote?.title]);

  // 调试：检查权限判断
  useEffect(() => {
    if (selectedNote && user) {
      const isOwner = isNoteOwner(selectedNote, user);
      console.log('[KnowledgeCard] 权限检查:', {
        noteId: selectedNote.id,
        noteOwnerId: selectedNote.owner_id || selectedNote.user_id,
        userId: user.id,
        username: user.username,
        isOwner: isOwner,
        noteTitle: selectedNote.title
      });
    }
  }, [selectedNote, user]);

  // 防双击保护 hooks
  const saveProtection = useDoubleClickProtection(3000); // 保存操作保护3秒
  const aiUpdateProtection = useDoubleClickProtection(5000); // AI更新操作保护5秒
  const confirmAiUpdateProtection = useDoubleClickProtection(5000); // 确认AI更新操作保护5秒
  const addTagProtection = useDoubleClickProtection(3000); // 添加标签操作保护3秒

  // 包装 handleAddTag 函数，添加防双击保护
  const handleAddTagWithProtection = useCallback(async () => {
    if (!addTagProtection.protect()) {
      message.warning(t('messages:operation_in_progress', { defaultValue: 'Add tag operation is in progress, please wait...' }));
      return;
    }

    try {
      await handleAddTag();
      // 操作完成后，延迟释放保护，确保UI状态已更新
      // 注意：由于 handleAddTag 内部已经处理了状态更新（关闭编辑状态等），
      // 这里延迟释放保护不会影响用户体验
      setTimeout(() => {
        addTagProtection.release();
      }, 500); // 延迟500ms释放，确保UI状态已更新
    } catch (error) {
      // 如果有未捕获的错误，立即释放保护，允许用户重试
      addTagProtection.release();
      console.error('[KnowledgeCard] 添加标签失败:', error);
    }
    // 注意：handleAddTag 内部已经处理了所有错误情况（通过 message.error），
    // 所以即使操作失败，也不会抛出错误，保护会在上面的 setTimeout 中释放
  }, [handleAddTag, addTagProtection, t]);

  // Topic Selector Modal state
  const [isTopicSelectorVisible, setIsTopicSelectorVisible] = useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
  const [selectedLevel0Topic, setSelectedLevel0Topic] = useState(null);
  const [selectedLevel1Topic, setSelectedLevel1Topic] = useState(null);
  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState([]);
  const [availableLevel0Topics, setAvailableLevel0Topics] = useState([]);
  const [availableLevel1Topics, setAvailableLevel1Topics] = useState([]);
  const [isAddingTopicRelation, setIsAddingTopicRelation] = useState(false);

  // Copy Notes Modal state
  const [isCopyToCollaborativeVisible, setIsCopyToCollaborativeVisible] = useState(false);
  const [copyTargetKnowledgeBase, setCopyTargetKnowledgeBase] = useState(null);
  const [copyAvailableKnowledgeBases, setCopyAvailableKnowledgeBases] = useState([]);
  const [copyTargetType, setCopyTargetType] = useState('collaborative');

  // Tags expand/collapse state (Figma design: show only 3 tags by default)
  const [showAllTags, setShowAllTags] = useState(false);

  // AI Tools expand/collapse state (show only 3 by default)
  const [showAllTools, setShowAllTools] = useState(false);

  // AI Update Modal state
  const [isAiUpdateModalVisible, setIsAiUpdateModalVisible] = useState(false);
  const [isTriggeringAiUpdate, setIsTriggeringAiUpdate] = useState(false);
  const [aiUpdateTooltipVisible, setAiUpdateTooltipVisible] = useState(false);

  // Topic selector function (defined before useImperativeHandle)
  const openAddTopicRelationSelector = useCallback(async () => {
    if (!selectedNote || !noteTopicHierarchy || noteTopicHierarchy.length === 0) {
      console.error(t('messages:please_select_note', { defaultValue: 'Please select a note first' }));
      return;
    }

    setIsAddingTopicRelation(true);
    setIsTopicSelectorVisible(true);
    
    // 使用第一个主题关联来确定笔记库
    const { knowledge_base } = noteTopicHierarchy[0];
    const kbs = await fetchAllKnowledgeBases();
    
    // 固定显示当前笔记所属的笔记库
    const currentKB = kbs.find(k => 
      (k.is_private && knowledge_base.is_private) || 
      (!k.is_private && k.kb_id === knowledge_base.kb_id)
    );
    
    if (currentKB) {
      setSelectedKnowledgeBase(currentKB);
      
      // 获取Level0主题
      const level0Topics = currentKB.topics.filter(topic => !topic.super_id);
      setAvailableLevel0Topics(level0Topics);
      
      // 清空当前选择（不预选择，让用户重新选择）
      setSelectedLevel0Topic(null);
      setSelectedLevel1Topic(null);
      setAvailableLevel1Topics([]);
    }
    
    // 设置可用笔记库列表只包含当前笔记库
    setAvailableKnowledgeBases([currentKB]);
  }, [selectedNote, noteTopicHierarchy, fetchAllKnowledgeBases, setIsAddingTopicRelation, setIsTopicSelectorVisible, setSelectedKnowledgeBase, setAvailableLevel0Topics, setSelectedLevel0Topic, setSelectedLevel1Topic, setAvailableLevel1Topics, setAvailableKnowledgeBases]);

  // Topic selector function for initial topic selection (when no topics exist)
  const openTopicSelector = useCallback(async () => {
    if (!selectedNote) {
      console.error(t('messages:please_select_note', { defaultValue: 'Please select a note first' }));
      return;
    }

    setIsAddingTopicRelation(false); // This is for selecting initial topic, not adding relation
    setIsTopicSelectorVisible(true);
    
    // Fetch all knowledge bases for selection
    const kbs = await fetchAllKnowledgeBases();
    setAvailableKnowledgeBases(kbs);
    
    // Clear previous selections
    setSelectedKnowledgeBase(null);
    setSelectedLevel0Topic(null);
    setSelectedLevel1Topic(null);
    setAvailableLevel0Topics([]);
    setAvailableLevel1Topics([]);
  }, [selectedNote, fetchAllKnowledgeBases, setIsAddingTopicRelation, setIsTopicSelectorVisible, setAvailableKnowledgeBases, setSelectedKnowledgeBase, setSelectedLevel0Topic, setSelectedLevel1Topic, setAvailableLevel0Topics, setAvailableLevel1Topics]);

  // Copy Notes Functions (moved from Dashboard.js)
  const openCopyToCollaborativeSelector = useCallback(async () => {
    if (!selectedNote) {
      message.error(t('messages:please_select_note'));
      return;
    }
    
    setCopyTargetType('collaborative');
    setIsCopyToCollaborativeVisible(true);
    
    try {
      // 获取可用的协作库（排除私有库）
      const allKBs = await fetchAllKnowledgeBases();
      const collaborativeKBs = allKBs.filter(kb => !kb.is_private);
      setCopyAvailableKnowledgeBases(collaborativeKBs);
      
      // 清空之前的选择
      setCopyTargetKnowledgeBase(null);
    } catch (error) {
      console.error('[前端] 获取协作库列表失败:', error);
      message.error(t('messages:fetch_collaborative_kb_failed', { defaultValue: 'Failed to fetch collaborative library list' }));
    }
  }, [selectedNote, fetchAllKnowledgeBases, t]);

  const openCopyToPrivateSelector = useCallback(async () => {
    if (!selectedNote) {
      message.error(t('messages:please_select_note'));
      return;
    }
    
    try {
      // 检查用户是否有私有库
      const privateResult = await apiHelpers.fetchPrivateKBTopics();
      if (!privateResult.hasPrivateKB) {
        message.error(t('messages:no_private_kb', { defaultValue: 'You do not have a private library yet, please create one first' }));
        return;
      }
      
      setCopyTargetType('private');
      setIsCopyToCollaborativeVisible(true);
      
      // 获取私有库信息
      const allKBs = await fetchAllKnowledgeBases();
      const privateKBs = allKBs.filter(kb => kb.is_private);
      setCopyAvailableKnowledgeBases(privateKBs);
      
      // 清空之前的选择
      setCopyTargetKnowledgeBase(null);
    } catch (error) {
      console.error('[前端] 获取私有库列表失败:', error);
      message.error(t('messages:fetch_private_kb_failed', { defaultValue: 'Failed to fetch private library' }));
    }
  }, [selectedNote, fetchAllKnowledgeBases, t]);

  const handleCopyKnowledgeBaseChange = useCallback((kbId) => {
    const kb = copyAvailableKnowledgeBases.find(k => k.kb_id === kbId);
    setCopyTargetKnowledgeBase(kb);
  }, [copyAvailableKnowledgeBases]);

  const cancelCopyToCollaborative = useCallback(() => {
    setIsCopyToCollaborativeVisible(false);
    setCopyTargetKnowledgeBase(null);
    setCopyAvailableKnowledgeBases([]);
    setCopyTargetType('collaborative');
  }, []);

  const confirmCopyToCollaborative = useCallback(async () => {
    if (!selectedNote) {
      message.error(t('messages:please_select_note'));
      return;
    }

    if (!copyTargetKnowledgeBase) {
      message.error(t('messages:please_select_target_kb', { defaultValue: 'Please select target knowledge library' }));
      return;
    }

    // 判断是复制到协作库还是私有库
    const isTargetPrivate = copyTargetKnowledgeBase.is_private;
    const targetTypeText = isTargetPrivate ? t('dashboard:private_kb', { defaultValue: 'Private Library' }) : t('dashboard:collaborative_kb', { defaultValue: 'Collaborative Library' });

    try {
      console.log('[前端] 开始复制笔记:', {
        noteId: selectedNote.id,
        targetKbId: copyTargetKnowledgeBase.kb_id,
        targetType: targetTypeText
      });

      // 构建请求体
      const requestBody = {
        noteId: selectedNote.id,
        targetKbId: copyTargetKnowledgeBase.kb_id
      };
      
      // 调用复制API
      const endpoint = isTargetPrivate ? '/notes/copy-to-private' : '/notes/copy-to-collaborative';
      const response = await apiClient.post(endpoint, requestBody);

      if (response.data.success) {
        message.success(t('messages:note_copied_success', { defaultValue: 'Note copied to {{type}} successfully!', type: targetTypeText }));
        
        console.log('[前端] 笔记复制成功，开始刷新左侧数据...');
        
        // 刷新左侧的私人笔记库和协作笔记库数量显示
        await refreshKnowledgeBasesAndTopics();
        
        console.log('[前端] 笔记复制和数据刷新完成');
        
        // 关闭选择器
        cancelCopyToCollaborative();
      } else {
        message.error(t('messages:copy_failed', { defaultValue: 'Copy failed: {{error}}', error: response.data.message || t('messages:unknown_error', { defaultValue: 'Unknown error' }) }));
      }
    } catch (error) {
      console.error('[前端] 笔记复制失败:', error);
      message.error(t('messages:copy_failed', { defaultValue: 'Copy failed: {{error}}', error: error.response?.data?.message || error.message }));
    }
  }, [selectedNote, copyTargetKnowledgeBase, refreshKnowledgeBasesAndTopics, cancelCopyToCollaborative, t]);

  // AI Update Functions
  const openAiUpdateModal = useCallback(() => {
    if (!selectedNote) {
      message.error(t('messages:please_select_note', { defaultValue: 'Please select a note first' }));
      return;
    }

    // 权限检查：只有笔记所有者才能触发AI更新
    if (!isNoteOwner(selectedNote, user)) {
      message.error(t('messages:no_permission_ai_update', { defaultValue: 'No permission: can only trigger AI update for your own notes' }));
      return;
    }

    setIsAiUpdateModalVisible(true);
  }, [selectedNote, user, t]);

  const cancelAiUpdate = useCallback(() => {
    setIsAiUpdateModalVisible(false);
    setIsTriggeringAiUpdate(false);
    confirmAiUpdateProtection.release(); // 取消时释放保护
  }, [confirmAiUpdateProtection]);

  const confirmAiUpdate = useCallback(async () => {
    if (!selectedNote) {
      message.error(t('messages:please_select_note', { defaultValue: 'Please select a note first' }));
      confirmAiUpdateProtection.release(); // 释放保护
      return;
    }

    setIsTriggeringAiUpdate(true);

    try {
      console.log('[前端] 开始触发笔记AI更新:', {
        noteId: selectedNote.id,
        userId: user?.id
      });

      // 调用AI更新API
      const response = await apiClient.post(`/notes/${selectedNote.id}/trigger-ai-update`);

      if (response.data.success) {
        message.success(t('messages:ai_update_triggered_success', { defaultValue: 'AI update task triggered successfully! The note will be analyzed in the background.' }));
        
        console.log('[前端] AI更新任务触发成功');
        
        // 关闭模态框
        cancelAiUpdate();
      } else {
        message.error(t('messages:ai_update_triggered_failed', { defaultValue: 'Failed to trigger AI update: {{error}}', error: response.data.message || t('messages:unknown_error', { defaultValue: 'Unknown error' }) }));
        confirmAiUpdateProtection.release(); // 失败时释放保护
      }
    } catch (error) {
      console.error('[前端] 触发AI更新失败:', error);
      message.error(t('messages:ai_update_triggered_failed', { defaultValue: 'Failed to trigger AI update: {{error}}', error: error.response?.data?.message || error.message }));
      confirmAiUpdateProtection.release(); // 失败时释放保护
    } finally {
      setIsTriggeringAiUpdate(false);
    }
  }, [selectedNote, user, cancelAiUpdate, confirmAiUpdateProtection, t]);

  // Handle removing topic relation (moved from Dashboard.js)
  const handleRemoveTopicRelation = useCallback(async (topicId, topicPath) => {
    if (!selectedNote || !topicId) {
      message.error(t('messages:invalid_parameters', { defaultValue: 'Invalid parameters' }));
      return;
    }

    try {
      // 显示确认对话框
      const confirmed = window.confirm(t('messages:confirm_remove_topic_relation', { defaultValue: 'Are you sure to remove topic relation "{{path}}"?', path: topicPath }));
      if (!confirmed) return;

      console.log('[前端] 开始移除笔记主题关联:', {
        noteId: selectedNote.id,
        topicId: topicId
      });

      // 调用删除主题关联API
      const response = await apiClient.delete(`/notes/${selectedNote.id}/topics/${topicId}`);

      if (response.data.success) {
        message.success(t('messages:topic_relation_removed', { defaultValue: 'Topic relation removed' }));
        
        // 刷新左侧的私人笔记库和协作笔记库数量显示
        await refreshKnowledgeBasesAndTopics();
        
        console.log('[前端] 主题关联移除和数据刷新完成');
      } else {
        message.error(t('messages:remove_topic_relation_failed', { defaultValue: 'Failed to remove topic relation: {{error}}', error: response.data.message || t('messages:unknown_error', { defaultValue: 'Unknown error' }) }));
      }
    } catch (error) {
      console.error('移除主题关联失败:', error);
      message.error(t('messages:remove_topic_relation_failed', { defaultValue: 'Failed to remove topic relation: {{error}}', error: error.response?.data?.message || error.message }));
    }
  }, [selectedNote, refreshKnowledgeBasesAndTopics]);

  // Handle removing tag from note
  const handleRemoveTag = useCallback(async (tag) => {
    if (!selectedNote || !tag) {
      message.error(t('messages:invalid_parameters', { defaultValue: 'Invalid parameters' }));
      return;
    }

    // 权限检查：只有笔记所有者才能删除标签
    if (!isNoteOwner(selectedNote, user)) {
      message.error(t('messages:no_permission_delete_tag', { defaultValue: 'No permission: can only delete tags from your own notes' }));
      return;
    }

    try {
      // 显示确认对话框
      const confirmed = window.confirm(t('messages:confirm_remove_tag', { defaultValue: 'Are you sure to remove tag "{{name}}" from the note?', name: tag.name }));
      if (!confirmed) return;

      console.log('[前端] 开始移除笔记标签:', {
        noteId: selectedNote.id,
        tagId: tag.id,
        tagName: tag.name
      });

      // 从笔记的标签列表中移除该标签
      const updatedTags = selectedNote.tags.filter(t => t.id !== tag.id);
      
      // 调用更新笔记API
      const response = await apiClient.put(`/notes/${selectedNote.id}`, {
        tags: updatedTags.map(t => t.name) // 传递标签名称数组
      });

      if (response.data) {
        message.success(t('messages:tag_removed', { defaultValue: 'Tag removed' }));
        
        // 更新选中的笔记状态
        setSelectedNote(prev => ({ ...prev, tags: updatedTags }));
        
        // 刷新历史标签列表
        if (handleFetchUserHistoryTags) {
          await handleFetchUserHistoryTags();
        }
        
        console.log('[前端] 标签移除成功');
      } else {
        message.error(t('messages:remove_tag_failed_backend', { defaultValue: 'Failed to remove tag: backend error' }));
      }
    } catch (error) {
      console.error('移除标签失败:', error);
      message.error(t('messages:remove_tag_failed', { defaultValue: 'Failed to remove tag: {{error}}', error: error.response?.data?.message || error.message }));
    }
  }, [selectedNote, user, setSelectedNote, handleFetchUserHistoryTags]);

  // Function registration removed - renderTopicHierarchy now calls openTopicSelector directly

  // Topic selector functionality is now self-contained in this component

  // Render topic hierarchy - Figma style with compact badges
  const renderTopicHierarchy = useCallback(() => {
    return (
      <div className="note-topic-hierarchy" style={{ marginBottom: '16px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', width: '100%' }}>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>
            {t('dashboard:topics', { defaultValue: 'Topics' })}:
          </span>

          {(!noteTopicHierarchy || noteTopicHierarchy.length === 0) ? (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('dashboard:no_topic_assigned', { defaultValue: 'No topic assigned' })}
            </Text>
          ) : (
            noteTopicHierarchy.map((topicRelation, index) => {
              const { hierarchy } = topicRelation;
              const lastTopic = hierarchy[hierarchy.length - 1];
              const displayText = lastTopic?.name || '';
              const bottomTopicId = lastTopic?.topic_id;

              return (
                <Tag
                  key={index}
                  closable={noteTopicHierarchy.length > 1 && isNoteOwner(selectedNote, user)}
                  onClose={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveTopicRelation(bottomTopicId, displayText);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    paddingRight: noteTopicHierarchy.length > 1 && isNoteOwner(selectedNote, user) ? '4px' : '8px',
                    fontSize: '12px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px',
                    color: '#1d4ed8',
                    margin: 0
                  }}
                >
                  <LinkOutlined style={{ fontSize: '12px' }} />
                  {displayText}
                </Tag>
              );
            })
          )}

          {/* Add topic button - only visible to owner */}
          {selectedNote && user && isNoteOwner(selectedNote, user) && (
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined style={{ fontSize: '12px' }} />}
              onClick={noteTopicHierarchy && noteTopicHierarchy.length > 0 ? openAddTopicRelationSelector : openTopicSelector}
              style={{
                height: '24px',
                padding: '0 8px',
                fontSize: '12px',
                fontWeight: 500,
                color: '#6b7280',
                textTransform: 'none'
              }}
            >
              Add
            </Button>
          )}
        </div>
      </div>
    );
  }, [noteTopicHierarchy, selectedNote, user, openTopicSelector, openAddTopicRelationSelector, handleRemoveTopicRelation, t]);

  // Handler functions for Topic Selector
  const handleKnowledgeBaseChange = (kbId) => {
    const kb = availableKnowledgeBases.find(k => k.kb_id === kbId);
    setSelectedKnowledgeBase(kb);
    setSelectedLevel0Topic(null);
    setSelectedLevel1Topic(null);
    
    if (kb && kb.topics) {
      const level0Topics = kb.topics.filter(topic => !topic.super_id);
      setAvailableLevel0Topics(level0Topics);
    } else {
      setAvailableLevel0Topics([]);
    }
    setAvailableLevel1Topics([]);
  };

  const handleLevel0TopicChange = (topicId) => {
    const topic = availableLevel0Topics.find(t => t.topic_id === topicId);
    setSelectedLevel0Topic(topic);
    setSelectedLevel1Topic(null);
    
    if (topic && topic.children && topic.children.length > 0) {
      setAvailableLevel1Topics(topic.children);
    } else {
      setAvailableLevel1Topics([]);
    }
  };

  const handleLevel1TopicChange = (topicId) => {
    const topic = availableLevel1Topics.find(t => t.topic_id === topicId);
    setSelectedLevel1Topic(topic);
  };

  // Duplicate function removed - using useCallback version above

  const cancelTopicSelection = () => {
    setIsTopicSelectorVisible(false);
    setSelectedKnowledgeBase(null);
    setSelectedLevel0Topic(null);
    setSelectedLevel1Topic(null);
    setAvailableKnowledgeBases([]);
    setAvailableLevel0Topics([]);
    setAvailableLevel1Topics([]);
    setIsAddingTopicRelation(false);
  };

  // const confirmTopicSelection1 = async () => {
  //   if (!selectedNote) {
  //     message.error(t('messages:please_select_note'));
  //     return;
  //   }

  //   // 确定最终选择的主题ID
  //   let finalTopicId = null;
  //   if (selectedLevel1Topic) {
  //     finalTopicId = selectedLevel1Topic.topic_id;
  //   } else if (selectedLevel0Topic) {
  //     finalTopicId = selectedLevel0Topic.topic_id;
  //   }

  //   try {
  //     if (isAddingTopicRelation) {
  //       // 新增主题关联模式
  //       console.log('[前端] 开始新增笔记主题关联:', {
  //         noteId: selectedNote.id,
  //         newTopicId: finalTopicId
  //       });

  //       // 调用新增主题关联API
  //       const response = await apiClient.post(`/notes/${selectedNote.id}/topics`, {
  //         topic_id: finalTopicId
  //       });

  //       if (response.data.success) {
  //         message.success('主题关联添加成功');
          
  //         // 重新获取笔记信息
  //         const noteResponse = await apiClient.get(`/notes/${selectedNote.id}`);
  //         if (noteResponse.data.success) {
  //           setSelectedNote(noteResponse.data.data);
  //         }
          
  //         // 刷新主题层级信息
  //         await fetchNoteTopicHierarchy(selectedNote.id);
          
  //         console.log('[前端] 主题关联添加成功，开始刷新左侧数据...');
          
  //         // 刷新左侧的私人笔记库和协作笔记库数量显示
  //         await refreshKnowledgeBasesAndTopics();
          
  //         console.log('[前端] 主题关联添加和数据刷新完成');
          
  //         // 关闭选择器
  //         cancelTopicSelection();
  //       } else {
  //         message.error('添加主题关联失败：' + (response.data.message || '未知错误'));
  //       }
  //     } else {
  //       // 更新主题归属模式（原有逻辑）
  //       console.log('[前端] 开始更新笔记主题归属:', {
  //         noteId: selectedNote.id,
  //         oldTopicIds: selectedNote.topicMappings ? selectedNote.topicMappings.map(m => m.topic_id) : [],
  //         newTopicId: finalTopicId
  //       });

  //       // 更新笔记的主题归属
  //       const updateResult = await updateNote(selectedNote.id, {
  //         topic_id: finalTopicId
  //       });

  //       if (updateResult.success) {
  //         message.success('主题归属更新成功');
  //         setSelectedNote(updateResult.data);
          
  //         // 刷新主题层级信息
  //         await fetchNoteTopicHierarchy(selectedNote.id);
          
  //         console.log('[前端] 主题归属更新成功，开始刷新左侧数据...');
          
  //         // 刷新左侧的私人笔记库和协作笔记库数量显示
  //         await refreshKnowledgeBasesAndTopics();
          
  //         // 如果当前正在显示特定主题的笔记列表，需要重新加载
  //         if (selectedTopic) {
  //           console.log('[前端] 检测到当前有主题筛选，重新加载主题笔记列表');
  //           await fetchNotes(1, 10, [], true, selectedTopic.topic_id, false, 'all');
  //         } else if (selectedFolder !== 'all') {
  //           console.log('[前端] 检测到当前有文件夹筛选，重新加载笔记列表');
  //           const currentTags = selectedTag ? [selectedTag.name] : [];
  //           const filterType = selectedFolder === 'private' ? 'private' : 
  //                             selectedFolder === 'collaborative' ? 'collaborative' :
  //                             selectedFolder === 'pinned' ? 'pinned' :
  //                             selectedFolder === 'recent' ? 'recent' : 'all';
  //           await fetchNotes(1, 10, currentTags, true, null, false, filterType);
  //         }
          
  //         console.log('[前端] 主题归属更新和数据刷新完成');
          
  //         // 关闭选择器
  //         cancelTopicSelection();
  //       } else {
  //         message.error('更新失败：' + (updateResult.message || '未知错误'));
  //       }
  //     }
  //   } catch (error) {
  //     console.error(isAddingTopicRelation ? '新增主题关联失败:' : '更新主题归属失败:', error);
  //     message.error((isAddingTopicRelation ? '新增主题关联失败：' : '更新失败：') + (error.response?.data?.message || error.message));
  //   }
  // };

  const confirmTopicSelection = async () => {
    if (!selectedNote) {
      message.error(t('messages:please_select_note', { defaultValue: 'Please select a note first' }));
      return;
    }

    // Determine final topic ID
    let finalTopicId = null;
    if (selectedLevel1Topic) {
      finalTopicId = selectedLevel1Topic.topic_id;
    } else if (selectedLevel0Topic) {
      finalTopicId = selectedLevel0Topic.topic_id;
    }

    if (!finalTopicId) {
      message.error(t('messages:please_select_topic', { defaultValue: 'Please select a topic' }));
      return;
    }

    try {
      console.log('[KnowledgeCard] 确认主题选择:', {
        noteId: selectedNote.id,
        topicId: finalTopicId,
        isAddingRelation: isAddingTopicRelation
      });

      // API call to add/update topic relation
      const response = await apiClient.post(`/notes/${selectedNote.id}/topics`, {
        topic_id: finalTopicId
      });

      if (response.data.success) {
        message.success(t('messages:topic_relation_added', { defaultValue: 'Topic relation added successfully' }));
        
        // Refresh knowledge bases and topics data
        await refreshKnowledgeBasesAndTopics();
        
        console.log('[KnowledgeCard] 主题关联更新成功');
      } else {
        message.error(t('messages:topic_relation_failed', { defaultValue: 'Topic relation failed: {{error}}', error: response.data.message || t('messages:unknown_error', { defaultValue: 'Unknown error' }) }));
      }
      
      // Close modal
      cancelTopicSelection();
    } catch (error) {
      console.error('[KnowledgeCard] 主题选择失败:', error);
      message.error(t('messages:topic_relation_failed', { defaultValue: 'Topic relation failed: {{error}}', error: error.response?.data?.message || error.message }));
    }
  };

  // Handle opening AI tool in NoteContentPage
  const handleOpenTool = useCallback((toolId) => {
    // AI Writer opens inline in the right panel
    if (toolId === 'aiwriter' && onRightPanelModeChange) {
      onRightPanelModeChange('aiwriter');
      return;
    }

    const noteId = selectedNote?.id?.toString();
    if (!noteId) return;

    sessionStorage.setItem('noteContent_noteId', noteId);
    sessionStorage.setItem('noteContent_tool', toolId);

    let noteContentWindow = window.open('/note-content', 'noteContent');
    if (noteContentWindow) {
      try {
        if (noteContentWindow.location.href.includes('/note-content')) {
          noteContentWindow.postMessage({
            type: 'noteIdChanged',
            noteId: noteId,
            tool: toolId
          }, window.location.origin);
        }
      } catch (e) {
        // Ignore cross-origin errors
      }
      noteContentWindow.focus();
    }
  }, [selectedNote, onRightPanelModeChange]);

  // Equalize tool button widths to the widest button
  useEffect(() => {
    const container = toolButtonsContainerRef.current;
    if (!container) return;
    const buttons = container.querySelectorAll('button');
    if (buttons.length === 0) return;
    buttons.forEach(btn => { btn.style.width = 'auto'; });
    let maxWidth = 0;
    buttons.forEach(btn => { maxWidth = Math.max(maxWidth, btn.offsetWidth); });
    buttons.forEach(btn => { btn.style.width = `${maxWidth}px`; });
  }, [selectedNote?.id, t, showAllTools]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="preview-header" style={{ position: 'relative', flexShrink: 0 }}>
        {/* When editing: Save/Cancel buttons first, then Edit Title label + textarea */}
        {isEditing && isNoteOwner(selectedNote, user) && (
          <>
            <div className="preview-actions" style={{
              display: 'flex',
              gap: '2px',
              justifyContent: 'flex-start',
              marginBottom: '12px'
            }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => {
                  if (saveProtection.protect()) {
                    handleSave(editTitle, editSummary, editContent);
                  } else {
                    message.warning(t('messages:operation_in_progress', { defaultValue: 'Save operation is in progress, please wait...' }));
                  }
                }}
                disabled={saveProtection.isProtected}
                loading={saveProtection.isProtected}
                size="small"
                className="gradient-button"
              >
                {saveProtection.isProtected
                  ? t('messages:saving', { defaultValue: 'Saving...' })
                  : t('common:actions.save', { defaultValue: 'Save' })
                }
              </Button>
              <Button size="small" icon={<CloseOutlined />} onClick={handleCancel}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
            </div>
            <Card style={{ marginBottom: 16 }} title={t('dashboard:edit_title', { defaultValue: 'Edit Title' })} size="small">
              <Input.TextArea
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  height: '120px',
                  resize: 'vertical'
                }}
                placeholder={t('dashboard:enter_topic', { defaultValue: 'Enter topic' })}
              />
            </Card>
          </>
        )}

        {/* When not editing: Title first, then action buttons */}
        {!isEditing && (
          <>
            <div className="preview-title" style={{ marginBottom: '12px' }}>
              <Title level={4} style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 600,
                color: '#111827',
                lineHeight: 1.4,
                display: 'flex',
                alignItems: 'center',
              }}>
                {getNoteIcon(selectedNote, { fontSize: '18px', marginRight: '8px' })}
                {selectedNote?.title}
              </Title>
            </div>
          </>
        )}

        {/* Figma Design: Action buttons as small ghost buttons (non-editing mode) */}
        {!isEditing && (
        <div className="preview-actions" style={{
          display: 'flex',
          gap: '2px',
          justifyContent: 'flex-start'
        }}>
          {(
            <>
              {/* 权限检查：只有笔记所有者才能看到以下操作按钮 */}
              {selectedNote && user && isNoteOwner(selectedNote, user) && (
                <>
                  {/* 刷新按钮 - Figma style ghost button */}
                  <Tooltip title={<span style={{ color: '#000' }}>{t('dashboard:refresh_note_content', { defaultValue: 'Refresh note content' })}</span>} color="#fff">
                    <Button
                      type="text"
                      icon={<ReloadOutlined />}
                      onClick={handleRefreshNote}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        color: '#6b7280',
                        border: 'none'
                      }}
                    />
                  </Tooltip>

                  {/* Star/Favorite 按钮 - Figma style ghost button */}
                  <Tooltip title={<span style={{ color: '#000' }}>{selectedNote?.is_pinned ? t('dashboard:unpin', { defaultValue: 'Unfavorite' }) : t('dashboard:pin', { defaultValue: 'Favorite' })}</span>} color="#fff">
                    <Button
                      type="text"
                      icon={selectedNote?.is_pinned ? <StarFilled /> : <StarOutlined />}
                      onClick={handlePin}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        color: selectedNote?.is_pinned ? '#f59e0b' : '#6b7280',
                        border: 'none'
                      }}
                    />
                  </Tooltip>

                  {/* 编辑按钮 - Figma style ghost button */}
                  <Tooltip
                    title={<span style={{ color: '#000' }}>{t('common:actions.edit', { defaultValue: 'Edit' })}</span>}
                    color="#fff"
                    visible={editTooltipVisible}
                    onVisibleChange={setEditTooltipVisible}
                  >
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditTooltipVisible(false);
                        handleEdit();
                      }}
                      onMouseEnter={() => setEditTooltipVisible(true)}
                      onMouseLeave={() => setEditTooltipVisible(false)}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        color: '#6b7280',
                        border: 'none'
                      }}
                    />
                  </Tooltip>

                  {/* Share/AI更新按钮 - Figma style ghost button */}
                  <Tooltip
                    title={<span style={{ color: '#000' }}>{t('dashboard:trigger_ai_update', { defaultValue: 'Run AI Analysis' })}</span>}
                    color="#fff"
                    visible={aiUpdateTooltipVisible}
                    onVisibleChange={setAiUpdateTooltipVisible}
                  >
                    <Button
                      type="text"
                      icon={<WandIcon />}
                      onClick={() => {
                        if (aiUpdateProtection.protect()) {
                          setAiUpdateTooltipVisible(false);
                          openAiUpdateModal();
                        } else {
                          setAiUpdateTooltipVisible(false);
                          message.warning(t('messages:operation_in_progress', { defaultValue: 'AI update operation is in progress, please wait...' }));
                        }
                      }}
                      disabled={aiUpdateProtection.isProtected}
                      loading={aiUpdateProtection.isProtected}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        color: '#6b7280',
                        border: 'none'
                      }}
                    />
                  </Tooltip>

                  {/* 删除按钮 - Figma style ghost button */}
                  <Tooltip title={<span style={{ color: '#000' }}>{t('common:actions.delete', { defaultValue: 'Delete' })}</span>} color="#fff">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteNote}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        color: '#6b7280',
                        border: 'none'
                      }}
                    />
                  </Tooltip>
                </>
              )}

              {/* 如果不是所有者，显示只读提示 */}
              {selectedNote && user && !isNoteOwner(selectedNote, user) && (
                <Tooltip title={<span style={{ color: '#000' }}>{t('dashboard:readonly_note_tooltip', { defaultValue: 'This is a public note from another user, you can only view it' })}</span>} color="#fff">
                  <Button type="text" icon={<EyeOutlined />} disabled style={{ height: '32px', padding: '0 8px', color: '#9ca3af', fontSize: '12px' }}>
                    {t('dashboard:readonly', { defaultValue: 'Read Only' })}
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </div>
        )}
      </div>

      <div 
        className="note-content-preview" 
        style={{ 
          flex: 1, 
          overflowY: 'auto', 
          minHeight: 0,
          scrollbarWidth: 'none', /* Firefox */
          msOverflowStyle: 'none', /* IE and Edge */
        }}
      >
        <style>{`
          .note-content-preview::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
        `}</style>
        {isEditing ? (
          <div className="edit-content">
            <Card style={{ marginBottom: 16 }} title={t('dashboard:edit_summary', { defaultValue: 'Edit Summary' })} size="small">
              <TiptapEditor
                value={editSummary}
                onChange={setEditSummary}
                placeholder={t('dashboard:enter_summary', { defaultValue: 'Enter summary...' })}
                markdown
                readonlyHeadings
                style={{ minHeight: '400px' }}
              />
            </Card>
          </div>
        ) : (
          <div>
            {/* Figma Design: Topics section with compact badges - hidden for team libraries */}
            {noteTopicHierarchy && noteTopicHierarchy.length > 0 && noteTopicHierarchy[0].knowledge_base?.is_private && renderTopicHierarchy()}

            {/* Figma Design: Tags section with compact badges and expand/collapse */}
            <div className="note-tags-preview" style={{ marginBottom: '8px', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', width: '100%' }}>
                <TagOutlined style={{ color: '#9ca3af', fontSize: '14px' }} />
                {selectedNote?.tags && (() => {
                  const allTags = selectedNote.tags;
                  const visibleTags = showAllTags ? allTags : allTags.slice(0, 3);
                  const hasMoreTags = allTags.length > 3;

                  return (
                    <>
                      {visibleTags.map(tag => (
                        <Tag
                          key={tag.id}
                          closable={isNoteOwner(selectedNote, user)}
                          onClose={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveTag(tag);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '2px 8px',
                            paddingRight: isNoteOwner(selectedNote, user) ? '4px' : '8px',
                            fontSize: '12px',
                            color: '#374151',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: 400,
                            margin: 0
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            updateGlobalState({
                              searchKeyword: tag.name,
                              searchMode: 'hybrid'
                            }, 'KnowledgeCard-tagClick');
                            setIsUserSelectedNote(true);
                          }}
                          title={t('dashboard:click_to_search_tag', { defaultValue: 'Click to search tag: {{name}}', name: tag.name })}
                        >
                          <TranslateText text={tag.name} />
                        </Tag>
                      ))}
                      {/* More/Less button - Figma style */}
                      {hasMoreTags && (
                        <Button
                          type="text"
                          size="small"
                          icon={showAllTags ? <UpOutlined style={{ fontSize: '12px' }} /> : <DownOutlined style={{ fontSize: '12px' }} />}
                          onClick={() => setShowAllTags(!showAllTags)}
                          style={{
                            height: '24px',
                            padding: '0 8px',
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#374151',
                            textTransform: 'none'
                          }}
                        >
                          {showAllTags ? 'Less' : 'More'}
                        </Button>
                      )}
                      {/* Add tag button - visible when expanded OR when 3 or fewer tags */}
                      {(showAllTags || !hasMoreTags) && isNoteOwner(selectedNote, user) && !isAddingTag && (
                        <Button
                          type="text"
                          size="small"
                          icon={<PlusOutlined style={{ fontSize: '12px' }} />}
                          onClick={() => {
                            setIsAddingTag(true);
                            handleFetchUserHistoryTags();
                          }}
                          style={{
                            height: '24px',
                            padding: '0 8px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#6b7280',
                            textTransform: 'none'
                          }}
                        >
                          Add
                        </Button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* 添加标签输入框 - 显示在标签区域下方 */}
            {isAddingTag && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ position: 'relative' }}>
                  <Space size="small">
                    <Input
                      size="small"
                      placeholder={t('dashboard:enter_tag_name', { defaultValue: 'Enter tag name' })}
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onPressEnter={handleAddTagWithProtection}
                      style={{ width: '120px' }}
                      autoFocus
                      disabled={addTagProtection.isProtected}
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleAddTagWithProtection}
                      disabled={addTagProtection.isProtected}
                      loading={addTagProtection.isProtected}
                      style={{ height: '22px' }}
                    >
                      {addTagProtection.isProtected
                        ? t('messages:adding', { defaultValue: 'Adding...' })
                        : t('common:actions.confirm', { defaultValue: 'Confirm' })
                      }
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setIsAddingTag(false);
                        setNewTagName('');
                        setUserHistoryTags([]);
                      }}
                      style={{ height: '22px' }}
                    >
                      {t('common:actions.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  </Space>

                  {/* 历史标签列表 */}
                  {(userHistoryTags.length > 0 || isLoadingHistoryTags) && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      zIndex: 1000,
                      marginTop: '4px',
                      maxWidth: '400px',
                      padding: '8px',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      {isLoadingHistoryTags ? (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {t('dashboard:loading_history_tags', { defaultValue: 'Loading history tags...' })}
                        </Text>
                      ) : (
                        <>
                          <Text type="secondary" style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                            {t('dashboard:recent_30_tags', { defaultValue: 'Recent 30 tags (click to add quickly):' })}
                          </Text>
                          <Space wrap size="small">
                            {userHistoryTags.slice(0, 30).map(tag => (
                              <Tag
                                key={tag.id}
                                color="default"
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  margin: '2px',
                                  border: '1px dashed #d9d9d9',
                                  backgroundColor: '#f8f9fa'
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setNewTagName(tag.name);
                                }}
                                title={t('dashboard:click_to_fill_tag', { defaultValue: 'Click to fill tag name: {{name}}', name: tag.name })}
                              >
                                <TranslateText text={tag.name} /> {tag.count && `(${tag.count})`}
                              </Tag>
                            ))}
                            {userHistoryTags.length > 30 && (
                              <Text type="secondary" style={{ fontSize: '11px' }}>
                                {t('dashboard:and_more_tags', { defaultValue: 'and {{count}} more tags...', count: userHistoryTags.length })}
                              </Text>
                            )}
                          </Space>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Figma Design: Copy to Team/Private Library button */}
            {selectedNote && noteTopicHierarchy && noteTopicHierarchy.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                {noteTopicHierarchy[0].knowledge_base.is_private ? (
                  <Button
                    size="small"
                    icon={<CopyOutlined style={{ fontSize: '12px' }} />}
                    onClick={openCopyToCollaborativeSelector}
                    style={{
                      height: '32px',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '0 12px',
                      borderColor: '#d1d5db',
                      color: '#6b7280',
                      borderRadius: '6px',
                      textTransform: 'none'
                    }}
                  >
                    Copy to team library
                  </Button>
                ) : (
                  <Button
                    size="small"
                    icon={<CopyOutlined style={{ fontSize: '12px' }} />}
                    onClick={openCopyToPrivateSelector}
                    style={{
                      height: '32px',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '0 12px',
                      borderColor: '#d1d5db',
                      color: '#6b7280',
                      borderRadius: '6px',
                      textTransform: 'none'
                    }}
                  >
                    Copy to private library
                  </Button>
                )}
              </div>
            )}

            {/* Figma Design: Separator before Summary */}
            <div style={{ borderTop: '1px solid #e5e7eb', marginBottom: '24px' }} />

            {/* Figma Design: Summary section */}
            {selectedNote?.summary && (() => {
              const isAiAnalysisMessage = selectedNote.summary === "AI analysis is in progress, please check back later";

              if (isAiAnalysisMessage) {
                return (
                  <div style={{ marginBottom: '24px' }}>
                    <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.7', fontStyle: 'italic' }}>
                      {t('messages:ai_analysis_in_progress', { defaultValue: 'AI analysis is in progress, please check back later' })}
                    </p>
                  </div>
                );
              }

              // Parse sections from summary text
              // Format: ### Section Name\nContent...
              const summaryText = selectedNote.summary || '';
              const sectionRegex = /^#{1,6}\s+(.+)$/gm;
              const sections = [];
              let match;
              let firstContent = '';

              // Find all section markers
              const matches = [];
              while ((match = sectionRegex.exec(summaryText)) !== null) {
                matches.push({ name: match[1].trim(), index: match.index, endIndex: match.index + match[0].length });
              }

              if (matches.length === 0) {
                // No sections found, treat entire text as summary
                sections.push({ name: t('dashboard:summary', { defaultValue: 'Summary' }), content: summaryText.trim(), isFirst: true });
              } else {
                // Get content before first section marker (if any)
                if (matches[0].index > 0) {
                  firstContent = summaryText.substring(0, matches[0].index).trim();
                  if (firstContent) {
                    sections.push({ name: t('dashboard:summary', { defaultValue: 'Summary' }), content: firstContent, isFirst: true });
                  }
                }

                // Extract each section, filtering by visibility config
                for (let i = 0; i < matches.length; i++) {
                  const rawName = matches[i].name;
                  if (!isSectionVisibleOnCard(rawName)) {
                    continue;
                  }
                  const sectionName = getSectionDisplayName(rawName, t);
                  const startIndex = matches[i].endIndex;
                  const endIndex = i < matches.length - 1 ? matches[i + 1].index : summaryText.length;
                  const content = summaryText.substring(startIndex, endIndex).trim();

                  if (content) {
                    sections.push({
                      name: sectionName,
                      content: content,
                      isFirst: sections.length === 0
                    });
                  }
                }
              }

              return (
                <>
                  {sections.map((section, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '24px',
                        paddingTop: index > 0 ? '24px' : 0,
                        borderTop: index > 0 ? '1px solid #e5e7eb' : 'none'
                      }}
                    >
                      {/* Skip header for first section since it's already rendered above */}
                      {!section.isFirst && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <h3 style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#111827',
                            margin: 0
                          }}>
                            {section.name}
                          </h3>
                        </div>
                      )}
                      <div
                        className="prose prose-sm max-w-none summary-section-content"
                        style={{
                          margin: 0,
                          fontSize: '14px',
                          color: '#374151',
                          lineHeight: '1.7',
                          fontWeight: 400,
                          wordWrap: 'break-word'
                        }}
                        dangerouslySetInnerHTML={{ __html: renderMarkdownSafely(section.content) }}
                      />
                    </div>
                  ))}
                </>
              );
            })()}

          </div>
        )}
      </div>

      {/* AI Tools - always visible, pinned at bottom */}
      {selectedNote?.id && (() => {
        const allTools = [
          {
            id: 'chat',
            icon: <RobotOutlined style={{ fontSize: 20, color: '#fff' }} />,
            gradient: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
            hoverBorderColor: '#a5b4fc',
            hoverBgColor: 'rgba(99, 102, 241, 0.05)',
            title: t('dashboard:ai_chat', { defaultValue: 'AI Read Assistant' }),
            description: t('dashboard:ai_chat_desc', { defaultValue: 'Ask questions and get instant answers based on the content' })
          },
          {
            id: 'brainstorm',
            icon: <RocketOutlined style={{ fontSize: 20, color: '#fff' }} />,
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            hoverBorderColor: '#6ee7b7',
            hoverBgColor: 'rgba(16, 185, 129, 0.05)',
            title: t('dashboard:ai_brainstorm', { defaultValue: 'Ideate & Brainstorm' }),
            description: t('dashboard:ai_brainstorm_desc', { defaultValue: 'Expand ideas with AI knowledge and your own knowledge hub' })
          },
          {
            id: 'aiwriter',
            icon: <EditOutlined style={{ fontSize: 20, color: '#fff' }} />,
            gradient: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            hoverBorderColor: '#d8b4fe',
            hoverBgColor: 'rgba(168, 85, 247, 0.05)',
            title: t('dashboard:form.ai_writer_assistant', { defaultValue: 'AI Doc Writer' }),
            description: t('dashboard:ai_writer_desc', { defaultValue: 'Generate documents from your knowledge base' })
          },
          {
            id: 'quiz',
            icon: <BulbOutlined style={{ fontSize: 20, color: '#fff' }} />,
            gradient: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            hoverBorderColor: '#d8b4fe',
            hoverBgColor: 'rgba(168, 85, 247, 0.05)',
            title: t('dashboard:ai_quiz', { defaultValue: 'AI Quiz' }),
            description: t('dashboard:ai_quiz_desc', { defaultValue: 'Quiz yourself on your content' })
          }
        ];
        const visibleTools = showAllTools ? allTools : allTools.slice(0, 3);
        const hasMoreTools = allTools.length > 3;

        const renderToolButton = (tool) => (
          <button
            key={tool.id}
            onClick={() => handleOpenTool(tool.id)}
            style={{
              textAlign: 'left',
              textTransform: 'none',
              background: 'none',
              border: '2px solid #e5e7eb',
              borderRadius: 8,
              padding: 12,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = tool.hoverBorderColor;
              e.currentTarget.style.backgroundColor = tool.hoverBgColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: tool.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {tool.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', marginBottom: 2, fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                    {tool.title}
                  </h3>
                  {tool.id === 'aiwriter' && (
                    <a
                      href="/ai-writer-history"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        fontSize: 12, fontWeight: 600, color: '#a855f7', textDecoration: 'none',
                        padding: '2px 8px', borderRadius: 4,
                        backgroundColor: 'rgba(168, 85, 247, 0.08)',
                        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                      }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.15)';
                        // Reset parent button hover
                        const btn = e.currentTarget.closest('button');
                        if (btn) { btn.style.borderColor = '#e5e7eb'; btn.style.backgroundColor = 'transparent'; }
                      }}
                      onMouseLeave={(e) => {
                        e.stopPropagation();
                        e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.08)';
                      }}
                    >
                      {t('dashboard:aiwriter_job_status', { defaultValue: 'Doc Status' })}
                    </a>
                  )}
                </div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 400, color: '#4b5563', lineHeight: 1.625, whiteSpace: 'nowrap', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                  {tool.description}
                </p>
              </div>
            </div>
          </button>
        );

        return (
          <div style={{ flexShrink: 0, borderTop: '1px solid #e5e7eb', padding: '16px 0 0' }}>
            <div ref={toolButtonsContainerRef} style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {visibleTools.map(renderToolButton)}
            </div>
            {hasMoreTools && (
              <Button
                type="text"
                size="small"
                icon={showAllTools ? <UpOutlined style={{ fontSize: 12 }} /> : <DownOutlined style={{ fontSize: 12 }} />}
                onClick={() => setShowAllTools(!showAllTools)}
                style={{
                  marginTop: 8,
                  padding: '0 8px',
                  height: 24,
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#6b7280'
                }}
              >
                {showAllTools
                  ? t('common:actions.less', { defaultValue: 'Less' })
                  : t('common:actions.more_tools', { defaultValue: 'More tools' })}
              </Button>
            )}
          </div>
        );
      })()}

      {/* Topic Selector Modal */}
      <Modal
        open={isTopicSelectorVisible}
        onCancel={cancelTopicSelection}
        footer={null}
        width={600}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FolderOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span>{isAddingTopicRelation ? t('dashboard:add_topic_relation', { defaultValue: 'Add Topic Relation' }) : t('dashboard:select_topic_belonging', { defaultValue: 'Select Topic Belonging' })}</span>
          </div>
        }
      >
        <div style={{ padding: '16px 0' }}>
          {/* Knowledge Base Selection */}
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Text strong>{t('dashboard:select_kb', { defaultValue: 'Select Knowledge Library' })}<span style={{ color: 'red' }}>*</span>：</Text>
            </Col>
            <Col span={18}>
              <Select
                style={{ width: '100%' }}
                placeholder={t('dashboard:please_select_kb', { defaultValue: 'Please select knowledge library' })}
                value={selectedKnowledgeBase?.kb_id}
                onChange={handleKnowledgeBaseChange}
                disabled={isAddingTopicRelation}
                allowClear={!isAddingTopicRelation}
              >
                {availableKnowledgeBases.map(kb => (
                  <Option key={kb.kb_id} value={kb.kb_id}>
                    {kb.is_private ? t('dashboard:private_kb', { defaultValue: 'Private Library' }) : t('dashboard:collaborative_kb', { defaultValue: 'Collaborative Library' })} - {kb.name}
                  </Option>
                ))}
              </Select>
              {isAddingTopicRelation && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    💡 {t('dashboard:fixed_to_current_kb', { defaultValue: 'Fixed to current note\'s knowledge library' })}
                  </Text>
                </div>
              )}
            </Col>
          </Row>

          {/* Level 0 Topic Selection */}
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Text strong>{t('dashboard:level0_topic', { defaultValue: 'Level 0 Topic' })}<span style={{ color: 'red' }}>*</span>：</Text>
            </Col>
            <Col span={18}>
              <Select
                style={{ width: '100%' }}
                placeholder={t('dashboard:please_select_level0_topic', { defaultValue: 'Please select Level 0 topic (required)' })}
                value={selectedLevel0Topic?.topic_id}
                onChange={handleLevel0TopicChange}
                disabled={!selectedKnowledgeBase}
                allowClear
              >
                {availableLevel0Topics.map(topic => (
                  <Option key={topic.topic_id} value={topic.topic_id}>
                    <TranslateText text={topic.name} />
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Level 1 Topic Selection */}
          <Row align="middle" gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Text strong>{t('dashboard:level1_topic', { defaultValue: 'Level 1 Topic' })}：</Text>
            </Col>
            <Col span={18}>
              <Select
                style={{ width: '100%' }}
                placeholder={t('dashboard:please_select_level1_topic', { defaultValue: 'Please select Level 1 topic (optional)' })}
                value={selectedLevel1Topic?.topic_id}
                onChange={handleLevel1TopicChange}
                disabled={!selectedLevel0Topic || availableLevel1Topics.length === 0}
                allowClear
              >
                {availableLevel1Topics.map(topic => (
                  <Option key={topic.topic_id} value={topic.topic_id}>
                    <TranslateText text={topic.name} />
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Selection Preview */}
          {(selectedKnowledgeBase || selectedLevel0Topic || selectedLevel1Topic) && (
            <div style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: '#f0f2f5', 
              borderRadius: '6px',
              borderLeft: '3px solid #667eea'
            }}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                {isAddingTopicRelation ? t('dashboard:add_topic_relation_preview', { defaultValue: 'Add Topic Relation Preview:' }) : t('dashboard:current_selection_preview', { defaultValue: 'Current Selection Preview:' })}
              </Text>
              <Text style={{ color: '#667eea' }}>
                {selectedKnowledgeBase && (
                  <>
                    {selectedKnowledgeBase.is_private ? t('dashboard:private_kb', { defaultValue: 'Private Library' }) : t('dashboard:collaborative_kb', { defaultValue: 'Collaborative Library' })}({selectedKnowledgeBase.name})
                    {selectedLevel0Topic && ` -> ${selectedLevel0Topic.name}`}
                    {selectedLevel1Topic && ` -> ${selectedLevel1Topic.name}`}
                  </>
                )}
                {!selectedKnowledgeBase && t('dashboard:no_topic_selected', { defaultValue: 'No topic selected' })}
              </Text>
              {isAddingTopicRelation && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    💡 {t('dashboard:add_topic_relation_tip', { defaultValue: 'This operation will add a new topic relation to the note without affecting existing topic relations' })}
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={cancelTopicSelection}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button 
                type="primary" 
                onClick={confirmTopicSelection}
                disabled={!selectedKnowledgeBase || !selectedLevel0Topic}
                className="gradient-button"
              >
                {isAddingTopicRelation ? t('dashboard:confirm_add_relation', { defaultValue: 'Confirm Add Relation' }) : t('dashboard:confirm_selection', { defaultValue: 'Confirm Selection' })}
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* AI Update Confirmation Modal */}
      <Modal
        open={isAiUpdateModalVisible}
        onCancel={cancelAiUpdate}
        footer={null}
        width={500}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BulbOutlined style={{ marginRight: 8, color: '#722ed1' }} />
            <span>{t('dashboard:trigger_ai_update', { defaultValue: 'Trigger AI Update' })}</span>
          </div>
        }
      >
        <div style={{ padding: '16px 0' }}>
          {/* 确认信息 */}
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#f9f0ff', 
            borderRadius: '6px',
            borderLeft: '3px solid #722ed1'
          }}>
            <Text strong style={{ display: 'block', marginBottom: '8px', color: '#722ed1' }}>
              {t('dashboard:ai_update_confirmation_title', { defaultValue: 'Confirm AI Analysis' })}
            </Text>
            <Text style={{ color: '#722ed1' }}>
              {t('dashboard:ai_update_confirmation_message', { defaultValue: 'You are about to re-analyze the note "{{title}}". The AI will generate updated topics, tags, and a summary. This process may take a few minutes to complete.', title: selectedNote?.title || 'Unknown' })}
            </Text>
          </div>

          {/* 说明信息 */}
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#e6f7ff', 
            borderRadius: '6px',
            borderLeft: '3px solid #1890ff'
          }}>
            <Text style={{ fontSize: '12px', color: '#1890ff' }}>
              💡 {t('dashboard:ai_update_description', { defaultValue: 'The analysis runs in the background. You can safely close this window and continue working.' })}
            </Text>
          </div>

          {/* 操作按钮 */}
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={cancelAiUpdate} disabled={isTriggeringAiUpdate}>
                {t('common:actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  if (confirmAiUpdateProtection.protect()) {
                    confirmAiUpdate();
                  } else {
                    message.warning(t('messages:operation_in_progress', { defaultValue: 'AI update confirmation is in progress, please wait...' }));
                  }
                }}
                loading={isTriggeringAiUpdate || confirmAiUpdateProtection.isProtected}
                disabled={isTriggeringAiUpdate || confirmAiUpdateProtection.isProtected}
                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
              >
                {(isTriggeringAiUpdate || confirmAiUpdateProtection.isProtected)
                  ? t('dashboard:triggering_ai_update', { defaultValue: 'Triggering...' })
                  : t('dashboard:confirm_trigger_ai_update', { defaultValue: 'Confirm Trigger AI Update' })
                }
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* Copy Notes Modal (moved from Dashboard.js) */}
      <Modal
        open={isCopyToCollaborativeVisible}
        onCancel={cancelCopyToCollaborative}
        footer={null}
        width={600}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CopyOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span>{t('dashboard:copy_note_to', { defaultValue: 'Copy Note to' })} {copyTargetType === 'collaborative' ? t('dashboard:collaborative_library', { defaultValue: 'Collaborative Library' }) : t('dashboard:private_library', { defaultValue: 'Private Library' })}</span>
          </div>
        }
      >
        <div style={{ padding: '16px 0' }}> 
          {/* 笔记库选择 */}
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Text strong>{t('dashboard:select', { defaultValue: 'Select' })} {copyTargetType === 'collaborative' ? t('dashboard:collaborative_library', { defaultValue: 'Collaborative Library' }) : t('dashboard:private_library', { defaultValue: 'Private Library' })}<span style={{ color: 'red' }}>*</span>：</Text>
            </Col>
            <Col span={18}>
              <Select
                style={{ width: '100%' }}
                placeholder={`${t('dashboard:please_select_target', { defaultValue: 'Please select target' })} ${copyTargetType === 'collaborative' ? t('dashboard:collaborative_library', { defaultValue: 'Collaborative Library' }) : t('dashboard:private_library', { defaultValue: 'Private Library' })}`}
                value={copyTargetKnowledgeBase?.kb_id}
                onChange={handleCopyKnowledgeBaseChange}
                allowClear
              >
                {copyAvailableKnowledgeBases.map(kb => (
                  <Select.Option key={kb.kb_id} value={kb.kb_id}>
                    {copyTargetType === 'collaborative' ? `${t('dashboard:collaborative_library', { defaultValue: 'Collaborative Library' })} - ${kb.name}` : t('dashboard:private_library', { defaultValue: 'Private Library' })}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* 说明信息 */}
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#e6f7ff', 
            borderRadius: '6px',
            borderLeft: '3px solid #1890ff'
          }}>
            <Text style={{ fontSize: '12px', color: '#1890ff' }}>
              💡 {t('dashboard:copy_note_description', { defaultValue: 'The note will be copied to the selected {{type}}, the original note remains unchanged. The copied note will {{visibility}}.', type: copyTargetType === 'collaborative' ? t('dashboard:collaborative_library', { defaultValue: 'Collaborative Library' }) : t('dashboard:private_library', { defaultValue: 'Private Library' }), visibility: copyTargetType === 'collaborative' ? t('dashboard:visible_to_members', { defaultValue: 'be visible to collaborative library members' }) : t('dashboard:become_private', { defaultValue: 'become your private note' }) })}
            </Text>
          </div>

          {/* 操作按钮 */}
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={cancelCopyToCollaborative}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button 
                type="primary" 
                onClick={confirmCopyToCollaborative}
                disabled={!copyTargetKnowledgeBase}
                className="gradient-button"
              >
                {t('dashboard:confirm_copy', { defaultValue: 'Confirm Copy' })}
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default KnowledgeCard;